import React, { useEffect, useRef, useState } from "react";
import { PatternLayer, enterStyles, exitStyles } from "@veyso/remotion";
import {
  filmstripCell,
  meshLayers,
  transitionTypes,
  type Background,
  type Transition,
  type TransitionType,
} from "@veyso/core";
import type { Filmstrip } from "./SyncPoint.js";

/**
 * The two faces a transition joins: what is leaving, and what is arriving.
 *
 * Nodes rather than colours because the point of the picker is that you see the
 * effect on your own footage. The caller builds them from whatever the boundary
 * actually is — a card on one side, a frame of the recording on the other.
 */
export interface BoundaryFaces {
  from: React.ReactNode;
  to: React.ReactNode;
}

/** A `Background` as CSS, for the small stand-ins the picker and its tiles draw. */
export const backgroundCss = (background: Background): React.CSSProperties => {
  if (background.kind === "color") return { background: background.color };
  if (background.kind === "gradient") {
    return { background: `linear-gradient(${background.angle}deg, ${background.from}, ${background.to})` };
  }
  // A mesh *is* a single declaration, so even the smallest stand-in gets the
  // real thing rather than a flat approximation of it.
  if (background.kind === "mesh") {
    return {
      backgroundColor: background.ground,
      backgroundImage: meshLayers(background.layout, background.fields).join(", "),
    };
  }
  // A pattern is drawn by `PatternLayer`, not by a single declaration; callers
  // that can render one should, and this stays the flat fallback.
  if (background.kind === "pattern") return { background: background.to };
  // The dim overlay is skipped: at this size it would darken the tile without
  // telling you anything the full-size preview does not.
  return { background: `#000 url(/assets/${background.assetKey}) center / cover` };
};

/**
 * A face showing one frame of the recording.
 *
 * Returns a flat panel when the contact sheet has not been built yet, which is
 * true for the first minute of a project's life and after a re-cut.
 */
export const recordingFace = (
  filmstrip: Filmstrip | null,
  seconds: number,
  durationSeconds: number,
): React.ReactNode => {
  if (!filmstrip) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-scope-600/40 text-[8px] font-semibold text-ink-200">
        BODY
      </div>
    );
  }
  // `TILE_WIDTH` and not the measured width: the tiles are a fixed size in a
  // fixed two-column grid, so there is nothing to measure and nothing that
  // changes when the panel does.
  const cell = filmstripCell(seconds, durationSeconds, filmstrip.width, filmstrip.height, TILE_WIDTH);
  return (
    <div
      className="h-full w-full bg-bay-900"
      style={{
        backgroundImage: `url(/assets/${filmstrip.key})`,
        backgroundSize: cell.backgroundSize,
        backgroundPosition: cell.backgroundPosition,
        backgroundRepeat: "no-repeat",
      }}
    />
  );
};

/** A face showing a HEAD or END card: its backdrop, and its title if it has one. */
export const cardFace = (background: Background, title: string | undefined): React.ReactNode => (
  <div
    className="relative flex h-full w-full items-center justify-center px-1"
    style={background.kind === "pattern" ? undefined : backgroundCss(background)}
  >
    {/* Drawn by the renderer's own component, so the tile shows the pattern
        rather than a flat approximation of it. */}
    {background.kind === "pattern" ? (
      <PatternLayer pattern={background.pattern} from={background.from} to={background.to} />
    ) : null}
    <span className="relative truncate text-[8px] font-bold leading-none text-white/90">{title || ""}</span>
  </div>
);

/** Tile width in pixels: two per row in the 296px properties panel, with gaps. */
const TILE_WIDTH = 124;

/**
 * The pose a tile holds when nothing is hovering it.
 *
 * Halfway through, which is where every effect shows most of its geometry — a
 * wipe half across, a slide half off. It is also where `fade` is at its
 * darkest, which is the honest picture of an effect that dips through black.
 */
const RESTING = 0.5;

/**
 * How dark the preview lets a veil get.
 *
 * `fade` reaches full black at its midpoint, and its resting pose is that
 * midpoint — so at full strength the tile is a black square in a grid of
 * pictures, which reads as a failed thumbnail rather than as an effect. Held
 * low enough that the frames stay visible through the dip, which is what
 * separates it from `crossfade` at a glance. The render is untouched; only this
 * thumbnail is capped.
 */
const MAX_VEIL = 0.55;

const LOOP_MS = 1600;
/** A beat of stillness at each end of the loop, so the start and finish read. */
const HOLD = 0.16;

const clamp01 = (value: number) => Math.min(1, Math.max(0, value));
/** Matches the `ease-in-out` default, not whatever easing the section is set to. */
const easeInOut = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

const prefersReducedMotion = () =>
  typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

export const transitionLabel = (type: TransitionType): string =>
  type.replace(/-/g, " ").replace(/^\w/, (c) => c.toUpperCase());

/**
 * One transition, drawn as the effect itself rather than named in a list.
 *
 * The styles come from the renderer's own `enterStyles` / `exitStyles`, so a
 * tile cannot show something the render will not do. Only the hovered tile
 * animates: eleven looping thumbnails is a grid that moves whenever you look at
 * it, and the one you are pointing at is the one you are asking about.
 */
const TransitionTile: React.FC<{
  type: TransitionType;
  faces: BoundaryFaces;
  selected: boolean;
  onSelect: () => void;
}> = ({ type, faces, selected, onSelect }) => {
  const [progress, setProgress] = useState(RESTING);
  const playing = useRef(false);
  const raf = useRef(0);

  useEffect(() => () => cancelAnimationFrame(raf.current), []);

  const play = () => {
    if (prefersReducedMotion() || playing.current) return;
    playing.current = true;
    const started = performance.now();
    const step = (now: number) => {
      if (!playing.current) return;
      const cycle = ((now - started) % LOOP_MS) / LOOP_MS;
      setProgress(easeInOut(clamp01((cycle - HOLD) / (1 - HOLD * 2))));
      raf.current = requestAnimationFrame(step);
    };
    raf.current = requestAnimationFrame(step);
  };

  const rest = () => {
    playing.current = false;
    cancelAnimationFrame(raf.current);
    setProgress(RESTING);
  };

  // `none` has no geometry to show. Drawing it at a progress would either be a
  // hard cut mid-frame or nothing at all; a plain split says "no effect".
  // Only `type` is read, but a whole `Transition` costs nothing and beats a cast.
  const demo: Transition = { type, duration: 0.6, easing: "ease-in-out" };
  const enter = enterStyles(demo, progress);
  const exit = exitStyles(demo, progress);
  const veil = enter.veil ?? exit.veil;

  return (
    <button
      onClick={onSelect}
      onPointerEnter={play}
      onPointerLeave={rest}
      onFocus={play}
      onBlur={rest}
      data-selected={selected}
      title={transitionLabel(type)}
      className="group flex flex-col gap-1 rounded-[6px] border border-[var(--edge)] p-1 text-left transition-colors hover:bg-bay-800 data-[selected=true]:border-ember-500 data-[selected=true]:bg-bay-750"
    >
      <span
        className="relative block w-full overflow-hidden rounded-[4px] bg-bay-900"
        style={{ aspectRatio: "16 / 9" }}
        aria-hidden
      >
        {type === "none" ? (
          <>
            <span className="absolute inset-y-0 left-0 w-1/2 overflow-hidden">{faces.from}</span>
            <span className="absolute inset-y-0 right-0 w-1/2 overflow-hidden">{faces.to}</span>
          </>
        ) : (
          <>
            <span className="absolute inset-0 overflow-hidden" style={exit.style}>
              {faces.from}
            </span>
            <span className="absolute inset-0 overflow-hidden" style={enter.style}>
              {faces.to}
            </span>
            {veil ? (
              <span
                className="absolute inset-0"
                style={{ ...veil, opacity: Math.min(MAX_VEIL, Number(veil.opacity ?? 1)) }}
              />
            ) : null}
          </>
        )}
      </span>
      <span className="truncate px-0.5 text-[10px] font-medium leading-none text-ink-300 group-hover:text-ink-100">
        {transitionLabel(type)}
      </span>
    </button>
  );
};

/**
 * Picking a transition by looking at it.
 *
 * This replaced a dropdown of eleven names. "Wipe" and "Circle reveal" and
 * "Blur" describe themselves badly enough that choosing between them meant
 * setting one, watching the preview, and going back — which is a picker built
 * out of trial and error.
 */
export const TransitionPicker: React.FC<{
  value: TransitionType;
  faces: BoundaryFaces;
  onChange: (type: TransitionType) => void;
}> = ({ value, faces, onChange }) => (
  <div className="flex flex-col gap-1.5">
    <span className="text-[11px] font-medium text-ink-300">Type</span>
    <div className="grid grid-cols-2 gap-1.5">
      {transitionTypes.map((type) => (
        <TransitionTile
          key={type}
          type={type}
          faces={faces}
          selected={type === value}
          onSelect={() => onChange(type)}
        />
      ))}
    </div>
  </div>
);
